import * as React from 'react';
import styled from 'styled-components';
import { Document, Page, pdfjs } from 'react-pdf';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import {
  getFormSetupFields,
  FormSetupType,
} from '../api/formSetup';
import { GET_ALL_QUERY_PARAMS } from '../constants';
import FormSetupFields from './FormSetupFields';
import { FormSetupFieldType } from '../types';
import {
  createResult,
  resolveFontSize,
  resolveMaxLines,
} from '../utils/formSetupUtils';
import {
  LEGACY_SCALE,
  fontPxToPt,
  scaleFromNaturalWidth,
  scaleFromPdfPageWidth,
  wrappedBoxHeight,
} from '../utils/formSetupCoords';
import PlaceholderCanvas from './PlaceholderEditor/PlaceholderCanvas';
import PlaceholderPropertiesPanel from './PlaceholderEditor/PlaceholderPropertiesPanel';
import PlaceholderPdfPreview from './PlaceholderEditor/PlaceholderPdfPreview';
import {
  MIN_CHIP_H,
  addedField,
  applyScale,
  containsField,
  fieldId,
} from './PlaceholderEditor/placeholderFields';
import {
  applyPreviewValues,
  loadPreviewValues,
  previewStorageKey,
  savePreviewValues,
} from './PlaceholderEditor/previewValues';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

// form_setup/models.py: font_size = models.SmallIntegerField(default=8).
const DEFAULT_FONT_PX = 8;

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.1;
const clampZoom = (z: number) =>
  Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z)) * 10) / 10;

const FormSetupPlaceholders = ({
  url,
  type,
  setPlaceholders,
  setMarkup,
  initialValues,
  initialActiveFields,
  handleSubmitPlaceholders,
  setSelectedFontSize,
  selectedFontSize,
}: Props) => {
  const [fields, setFields] = React.useState(null);
  const [activeFields, setActiveFields] = React.useState<
    Array<FormSetupFieldType>
  >([]);
  const [model, setModel] = React.useState('');
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [zoom, setZoom] = React.useState<number>(1);
  const [dirty, setDirty] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);
  // Only used to swap the button's label to "Saved" — a greyed-out button that
  // still reads "Save" looks broken rather than finished.
  const [savedOnce, setSavedOnce] = React.useState<boolean>(false);
  // Holds the markup the preview was opened with, so what is rendered is a
  // snapshot of the canvas at click time rather than something that re-fetches
  // on every subsequent nudge.
  const [previewMarkup, setPreviewMarkup] = React.useState<string | null>(null);
  const [pdfError, setPdfError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    setPdfError(null);
  }, [url]);

  // Image pixels per view pixel. Starts at the legacy constant because the
  // placeholders are handed to us before the form image has loaded; the real
  // ratio arrives with the image's natural size (see onImageLoad).
  const [scale, setScale] = React.useState<number>(LEGACY_SCALE);

  const previewKey = previewStorageKey(initialValues?.id);

  // What a placed value will actually print at, in view pixels (= points).
  //
  // Follows the picker rather than the saved value so the chips resize the
  // moment the font size is changed, before saving — same reason Preview PDF
  // reads `selectedFontSize` first. DEFAULT_FONT_PX matches the model's own
  // default (form_setup/models.py: font_size = SmallIntegerField(default=8)),
  // so a form that has never had one set is drawn the way it would print.
  //
  // Deliberately NOT clamped to some readable minimum: the point of this is
  // that the canvas stops lying about the output. A form set to 8 px really
  // does print at ~3pt, and seeing that is the fastest way to find out.
  const formFontPx = Number(
    selectedFontSize || initialValues?.fontSize || DEFAULT_FONT_PX,
  );

  const labelFontPx = fontPxToPt(formFontPx, scale);

  /**
   * What ONE placeholder prints at, in view pixels (= points).
   *
   * A placeholder may carry its own font_size, which the server resolves in
   * preference to the form's (form_setup/helpers.py::_node_fontsize). Reading
   * it the same way here is what keeps the canvas honest: Information set to
   * 12 while the form is on 30 has to look 12, or the box drawn around it is
   * measured against a size it will never print at.
   */
  const fieldFontPt = (field: any) => {
    const own = resolveFontSize(field?.fontSize);
    return own ? fontPxToPt(own, scale) : labelFontPx;
  };

  React.useEffect(() => {
    if (initialActiveFields?.length) {
      setActiveFields(
        applyPreviewValues(
          applyScale(initialActiveFields, scale),
          loadPreviewValues(previewKey),
        ),
      );
    }
  }, [initialActiveFields]);

  React.useEffect(() => {
    getFormSetupFields(GET_ALL_QUERY_PARAMS)
      .then(fieldsResponse => {
        if (type === 0) {
          setFields({
            fields: fieldsResponse.data.documents[0].fields,
          });
          setModel('Permit Documents');
        }
        if (type === 1) {
          setFields({
            fields: fieldsResponse.data.reports[0].fields,
          });
          setModel('User');
        }
      })
      .catch(err => console.log(err))
      .finally(() => {});
  }, []);

  // The true scale is only knowable once the browser has the image. Re-place
  // every stored placeholder from its ORIGINAL image pixels — recomputing from
  // the on-screen values would bake in the fallback scale's error.
  const applyNewScale = (next: number) => {
    if (next === scale) return;
    setScale(next);
    setActiveFields(prev => applyScale(prev, next));
  };

  const onImageLoad = (naturalWidth: number) =>
    applyNewScale(scaleFromNaturalWidth(naturalWidth));

  // The react-pdf path draws to a <canvas>, so onImageLoad never fires and the
  // scale would stay on the legacy fallback. The page's width in points gives
  // the true ratio to the raster Django will produce on save.
  const onPdfPageLoad = (page: any) =>
    applyNewScale(
      scaleFromPdfPageWidth(
        page?.originalWidth || page?.view?.[2],
      ),
    );

  // Marks the field as moved, which is what opts it back into the coordinate
  // maths on save — untouched fields re-emit their stored numbers verbatim so
  // they cannot drift a pixel per save cycle.
  const onMove = (field: any, patch: Record<string, number>) => {
    const id = fieldId(field);
    setActiveFields(prev =>
      prev.map(item =>
        (fieldId(item) === id ? { ...item, ...patch, stored: null } : item)));
    setDirty(true);
  };

  // Deliberately NOT routed through onMove: that clears `stored`, which opts a
  // field back into coordinate re-derivation. Typing a test value would then
  // recompute its position on every keystroke and drift it a pixel at a time.
  // A test value is not geometry, so it must not touch geometry.
  const onPreviewTextChange = (field: any, value: string) => {
    const id = fieldId(field);
    setActiveFields(prev =>
      prev.map(item =>
        (fieldId(item) === id ? { ...item, previewText: value } : item)));
  };

  /**
   * The box a wrapped placeholder should occupy, in view pixels.
   *
   * The renderer gives it `font_size * LINE_HEIGHT * max_lines` points and
   * clips the rest (pdf_render.py::_instruction_html), so recomputing the same
   * product here is what makes the chip on the canvas the footprint on the
   * page — raise Max lines and the box grows by exactly one line, change Font
   * Size and every wrapped box re-sizes with it.
   *
   * Clamped to MIN_CHIP_H only so a one-line box at a small font stays big
   * enough to grab with the mouse. That clamp cannot reach the printed form:
   * the server never reads the stored height, it derives its own from
   * max_lines.
   */
  const wrapHeight = (fontPt: number, maxLines: any) =>
    Math.max(
      MIN_CHIP_H,
      wrappedBoxHeight(fontPt, resolveMaxLines(maxLines)),
    );

  // Every wrapped placeholder re-sized to `fontPt`. Used when the Font Size
  // picker moves, which changes the height of all of them at once — except the
  // ones printing at their own size, whose box the form's size no longer says
  // anything about.
  const resizeWrapped = (placed: any[], fontPt: number) =>
    (placed || []).map(item =>
      (item?.multiline && !resolveFontSize(item?.fontSize)
        ? {
          ...item,
          height: wrapHeight(fontPt, item.maxLines),
          // `stored` re-emits the OLD box size verbatim on save (see
          // createResult), so it has to go or the new height is discarded.
          stored: null,
        }
        : item));

  // Print options (wrapping, max lines). Routed away from onMove for the same
  // reason as onPreviewTextChange — onMove takes a geometry patch and nothing
  // else. Unlike a test value these ARE saved with the form, so they mark the
  // editor dirty, and turning wrapping on (or changing Max lines) resizes the
  // box to match.
  const onOptionChange = (
    field: any,
    patch: { multiline?: boolean; maxLines?: string; fontSize?: string },
  ) => {
    const id = fieldId(field);
    setActiveFields(prev =>
      prev.map(item => {
        if (fieldId(item) !== id) return item;
        const next = { ...item, ...patch };
        // Switching wrapping OFF leaves the box where it is: the value goes
        // back to one unwrapped run, and there is no footprint to derive.
        if (!next.multiline) return next;
        // Measured from the PATCHED field, so picking a font size resizes the
        // box in the same keystroke rather than a render behind.
        return {
          ...next,
          height: wrapHeight(fieldFontPt(next), next.maxLines),
          stored: null,
        };
      }));
    setDirty(true);
  };

  const onDelete = (field: any) => {
    const id = fieldId(field);
    setActiveFields(prev => prev.filter(item => fieldId(item) !== id));
    if (selectedId === id) setSelectedId(null);
    setDirty(true);
  };

  // Font size is saved WITH the form (handleSubmitPlaceholders sends it
  // alongside the markup), so changing it alone is a real, savable edit and
  // has to enable Save. It used to call the parent's setter directly, which
  // never touched `dirty` — so picking a new size left the button greyed out
  // and the change could only be saved by also nudging a placeholder.
  //
  // Guarded on an actual change so that a select firing onChange while
  // mounting cannot mark a pristine form dirty.
  const onFontSizeChange = (value: any) => {
    setSelectedFontSize(value);
    if (Number(value) !== Number(selectedFontSize)) {
      setDirty(true);
      // A wrapped box is measured in LINES, so a new font size is a new box.
      // Deliberately here and not in an effect on `labelFontPx`: that also
      // changes when the image loads and the true scale replaces the fallback,
      // which would silently rewrite geometry on open, with no user action.
      setActiveFields(prev => resizeWrapped(prev, fontPxToPt(value, scale)));
    }
  };

  // The field picker owns add/remove, so anything it does is a change too.
  const onPickerChange = (next: any) => {
    const added = addedField(activeFields, next);

    setActiveFields(next);

    if (added) {
      // Select what was just added. Every new chip lands at the same default
      // spot, so without this it appears unselected and the X / Y / Width /
      // Height controls stay pointed at whatever was selected before — you
      // end up moving the wrong field, or hunting for the new one under the
      // old ones.
      setSelectedId(fieldId(added));
    } else if (!containsField(next, selectedId)) {
      // A second click on the picker removes a field; drop the selection with
      // it rather than leaving the panel bound to something that is gone.
      setSelectedId(null);
    }

    setDirty(true);
  };

  const onSave = async () => {
    const createdMarkup = createResult(activeFields, model, scale);

    // Edit-Placeholders path: the parent persists and the modal stays open, so
    // the button has to report the outcome itself.
    if (handleSubmitPlaceholders) {
      setSaving(true);
      const saved = await handleSubmitPlaceholders({ createdMarkup });
      setSaving(false);
      if (saved !== false) {
        // Clearing `dirty` is what greys the button out — there is nothing
        // left to save. It re-enables on the next edit. A failure leaves it
        // enabled so the work can be retried.
        setDirty(false);
        setSavedOnce(true);
      }
      return;
    }

    // Set-Placeholders path (inside the Add/Edit Form modal): nothing is
    // persisted here, the markup is just staged for the outer form's own save,
    // so closing is the right end state.
    setMarkup(createdMarkup);
    setPlaceholders(false);
  };

  // Sends the CURRENT canvas state, not what is stored, so a layout can be
  // checked before it is committed. The `true` is what lets test values
  // through — this is the only call site that passes it.
  const onPreview = () => {
    savePreviewValues(previewKey, activeFields);
    setPreviewMarkup(
      JSON.stringify(createResult(activeFields, model, scale, true)),
    );
  };

  const saveLabel = () => {
    if (saving) return 'Saving…';
    if (savedOnce && !dirty) return 'Saved';
    return 'Save';
  };

  return (
    <Container>
      <Main>
        <Toolbar>
          <ZoomGroup>
            <ToolBtn
              type="button"
              onClick={() => setZoom(z => clampZoom(z - ZOOM_STEP))}
              disabled={zoom <= ZOOM_MIN}
              aria-label="Zoom out"
              title="Zoom out"
            >
              <ZoomOutIcon style={{ fontSize: 18 }} />
            </ToolBtn>
            <ZoomLabel>{Math.round(zoom * 100)}%</ZoomLabel>
            <ToolBtn
              type="button"
              onClick={() => setZoom(z => clampZoom(z + ZOOM_STEP))}
              disabled={zoom >= ZOOM_MAX}
              aria-label="Zoom in"
              title="Zoom in"
            >
              <ZoomInIcon style={{ fontSize: 18 }} />
            </ToolBtn>
          </ZoomGroup>
          <Actions>
            {/* Available in both flows: an existing form renders against its
                saved page, a new one against the PDF still in the browser. */}
            {(initialValues?.id || url) && (
              <PreviewBtn
                type="button"
                onClick={onPreview}
                disabled={!activeFields?.length}
              >
                Preview PDF
              </PreviewBtn>
            )}
            <SaveBtn
              type="button"
              onClick={onSave}
              disabled={saving || !activeFields?.length || !dirty}
            >
              {saveLabel()}
            </SaveBtn>
          </Actions>
        </Toolbar>

        <PlaceholderCanvas
          fields={activeFields}
          // In edit mode the server has already rasterised the PDF, so the form
          // is a plain image. The react-pdf document is only for the create
          // flow, where the upload has not been persisted yet.
          imageSrc={initialValues && !url ? initialValues?.file : ''}
          zoom={zoom}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onMove={onMove}
          onDelete={onDelete}
          onImageLoad={onImageLoad}
          labelFontPx={labelFontPx}
          fieldFontPx={fieldFontPt}
        >
          {/* Only when there is genuinely something to render. `url` is empty
              until FileReader has produced the data URL, and react-pdf treats
              an empty file as a load failure rather than "nothing yet". */}
          {(!initialValues || url) && !!url && (
            <Document
              file={url}
              // react-pdf's stock message is the bare string "Failed to load
              // PDF file.", which is true of a corrupt upload, a blocked
              // pdf.worker.js and a version mismatch alike — three very
              // different fixes. Report what actually went wrong.
              onLoadError={setPdfError}
              onSourceError={setPdfError}
              error={
                <PdfError>
                  <b>Could not render this PDF.</b>
                  <span>
                    {pdfError?.message ||
                      'The file could not be read.'}
                  </span>
                </PdfError>
              }
            >
              <Page
                pageNumber={1}
                width={612}
                onLoadSuccess={onPdfPageLoad}
              />
            </Document>
          )}
        </PlaceholderCanvas>
      </Main>

      <Side>
        <PlaceholderPropertiesPanel
          fields={activeFields}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onMove={onMove}
          onDelete={onDelete}
          onPreviewTextChange={onPreviewTextChange}
          onOptionChange={onOptionChange}
          formFontPx={formFontPx}
        />
        <FormSetupFields
          fields={fields}
          setActiveFields={onPickerChange}
          activeFields={activeFields}
          setSelectedFontSize={onFontSizeChange}
          initialFont={initialValues?.fontSize}
        />
      </Side>

      {previewMarkup !== null && (
        <PlaceholderPdfPreview
          formId={initialValues?.id}
          dataUrl={url}
          formMarkup={previewMarkup}
          fontSize={selectedFontSize || initialValues?.fontSize}
          onClose={() => setPreviewMarkup(null)}
        />
      )}
    </Container>
  );
};

interface Props {
  url: string;
  type: number;
  setPlaceholders: (data: boolean) => void;
  setMarkup?: (data: any) => void;
  initialValues: FormSetupType;
  initialActiveFields?: any;
  /** Resolves false when the save failed, so the button stays enabled to retry. */
  handleSubmitPlaceholders?: (data: any) => Promise<boolean> | void;
  setSelectedFontSize?: (data: any) => void;
  /** Current picker value, so the preview uses an un-saved font change too. */
  selectedFontSize?: number;
}

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const Container = styled.section`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  font-family: ${FONT_STACK};

  @media only screen and (max-width: 1024px) {
    flex-direction: column;
  }
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

const Side = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 320px;
  flex: 0 0 auto;
  max-height: 72vh;
  overflow-y: auto;
  overflow-x: hidden;

  @media only screen and (max-width: 1024px) {
    width: 100%;
    max-height: none;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ZoomGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ZoomLabel = styled.span`
  min-width: 46px;
  text-align: center;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--neutral-600);
`;

const ToolBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-600);
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--primary-50);
    border-color: var(--primary-600);
    color: var(--primary-700);
  }

  &:disabled {
    color: var(--neutral-400);
    cursor: not-allowed;
  }
`;

const PdfError = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 12px 0;
  padding: 14px 16px;
  border: 1px solid var(--danger-100);
  border-radius: 8px;
  background: var(--danger-50);
  color: var(--neutral-700);
  font-size: 0.8125rem;
  line-height: 1.5;

  b {
    color: var(--danger-500);
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PreviewBtn = styled.button`
  min-width: 140px;
  height: 40px;
  padding: 0 18px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    color: var(--neutral-400);
    cursor: not-allowed;
  }
`;

const SaveBtn = styled.button`
  min-width: 140px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    background: var(--neutral-200);
    color: var(--neutral-500);
    cursor: not-allowed;
  }
`;

export default FormSetupPlaceholders;
